<template>
  <div class="settings--content inbox-quality-settings">
    <div
      v-if="uiFlags.isFetchingQualityHistory"
      class="inbox-quality-settings__loading"
    >
      <spinner size="small" />
      <span>{{ $t('INBOX_MGMT.QUALITY.LOADING') }}</span>
    </div>
    <template v-else>
      <div v-if="hasData" class="inbox-quality-settings__card">
        <div class="inbox-quality-settings__toolbar">
          <div class="button-group">
            <woot-button
              v-tooltip.top="$t('INBOX_MGMT.QUALITY.VIEW.CHART')"
              icon="arrow-trending-lines"
              :variant="viewMode === 'chart' ? '' : 'hollow'"
              color-scheme="primary"
              :aria-label="$t('INBOX_MGMT.QUALITY.VIEW.CHART')"
              @click="viewMode = 'chart'"
            />
            <woot-button
              v-tooltip.top="$t('INBOX_MGMT.QUALITY.VIEW.TABLE')"
              icon="db-table"
              :variant="viewMode === 'table' ? '' : 'hollow'"
              color-scheme="primary"
              :aria-label="$t('INBOX_MGMT.QUALITY.VIEW.TABLE')"
              @click="viewMode = 'table'"
            />
          </div>
          <woot-button
            icon="arrow-download"
            color-scheme="success"
            @click="exportQualityHistory"
          >
            {{ $t('INBOX_MGMT.QUALITY.EXPORT.BUTTON') }}
          </woot-button>
        </div>
        <QualityHistoryLineChart
          v-if="viewMode === 'chart'"
          :history="qualityHistory"
        />
        <QualityHistoryTable v-else :records="qualityRecords" />
      </div>
      <div v-else class="inbox-quality-settings__empty">
        <img
          class="inbox-quality-settings__illustration"
          src="~dashboard/assets/images/dev-productivity.svg"
          :alt="$t('INBOX_MGMT.QUALITY.EMPTY.MESSAGE')"
        />
        <p>
          {{ $t('INBOX_MGMT.QUALITY.EMPTY.MESSAGE') }}
        </p>
      </div>
    </template>
    <quality-disclaimer />
  </div>
</template>

<script>
import { mapGetters } from 'vuex';
import Spinner from 'shared/components/Spinner';
import alertMixin from 'shared/mixins/alertMixin';
import QualityHistoryLineChart from './QualityHistoryLineChart';
import QualityHistoryTable from './QualityHistoryTable';
import QualityDisclaimer from './QualityDisclaimer';
import { downloadCsvFile } from 'dashboard/helper/downloadHelper';
import {
  buildQualityHistoryCsv,
  qualityHistoryFileName,
} from '../helpers/qualityHistory';

export default {
  name: 'InboxQualitySettings',
  components: {
    Spinner,
    QualityHistoryLineChart,
    QualityHistoryTable,
    QualityDisclaimer,
  },
  mixins: [alertMixin],
  props: {
    inbox: {
      type: Object,
      required: true,
    },
  },
  data() {
    return {
      hasData: false,
      qualityHistory: [],
      qualityRecords: [],
      viewMode: 'chart',
    };
  },
  computed: {
    ...mapGetters({
      uiFlags: 'inboxes/getUIFlags',
    }),
  },
  watch: {
    'inbox.id': {
      immediate: false,
      handler() {
        this.resetState();
        this.fetchQualityHistory();
      },
    },
  },
  mounted() {
    this.fetchQualityHistory();
  },
  methods: {
    resetState() {
      this.hasData = false;
      this.qualityHistory = [];
      this.qualityRecords = [];
    },
    async fetchQualityHistory() {
      try {
        const data = await this.$store.dispatch('inboxes/getQualityHistory', {
          inboxId: this.inbox.id,
        });
        this.hasData = Boolean(data?.has_data && data?.history?.length);
        this.qualityHistory = data?.history || [];
        this.qualityRecords = data?.records || [];
      } catch {
        this.resetState();
        this.showAlert(this.$t('INBOX_MGMT.QUALITY.ERRORS.FETCH'));
      }
    },
    exportQualityHistory() {
      downloadCsvFile(
        qualityHistoryFileName(this.inbox.phone_number),
        buildQualityHistoryCsv(this.qualityRecords, key => this.$t(key))
      );
    },
  },
};
</script>

<style lang="scss" scoped>
@import '~dashboard/assets/scss/variables';

.inbox-quality-settings__loading {
  display: flex;
  align-items: center;
  gap: $space-small;
  padding: $space-large 0;
}

.inbox-quality-settings__card {
  position: relative;
  width: 100%;
  border-radius: 10px;
  box-shadow: 0px 0px 4px 0px rgba(0, 0, 0, 0.25);
  padding: 6rem;
  padding-top: 8em;

  ::v-deep .quality_history_line_chart {
    box-shadow: none;
    border-radius: 0;
    padding: 0;
  }
}

.inbox-quality-settings__toolbar {
  position: absolute;
  top: $space-normal;
  right: $space-normal;
  display: flex;
  align-items: center;
  gap: $space-normal;

  .button-group {
    margin-bottom: 0;
  }
}

.inbox-quality-settings__empty {
  display: flex;
  flex-direction: column;
  align-items: center;
  text-align: center;
  padding: $space-large $space-normal;
}

.inbox-quality-settings__illustration {
  max-width: 280px;
  width: 100%;
  margin-bottom: $space-normal;
}
</style>
